/**
 * Lightweight procedural SFX via Web Audio API (no external assets).
 */

const MASTER = 0.06;
const LOBBY_VOL = 0.22;
const STORM_AMB_VOL = 0.38;
const COMBAT_FOCUS_DECAY = 4.4;
const STORM_TICK_DECAY = 7.5;

import * as THREE from 'three';
import type { FootstepSurface } from './WorldBuilder.js';

type LoopVoice = {
  src: AudioBufferSourceNode | OscillatorNode;
  gain: GainNode;
};

export class FpsAudio {
  private ctx: AudioContext | null = null;
  /** 0–1 user volume multiplier (settings). */
  private masterMul = 0.85;
  private lobbyOut: GainNode | null = null;
  private lobbyStopping = false;
  private lobbyVoices: LoopVoice[] = [];
  private stormOut: GainNode | null = null;
  private stormOsc: OscillatorNode | null = null;
  private stormLfo: OscillatorNode | null = null;
  private stormNoiseSrc: AudioBufferSourceNode | null = null;
  private combatOut: GainNode | null = null;
  private combatFocus = 0;
  private stormTickFocus = 0;

  setMasterVolume(v: number): void {
    this.masterMul = Math.max(0, Math.min(1, v));
    this.applyMixGains();
  }

  getMasterVolume(): number {
    return this.masterMul;
  }

  /** Call from a user gesture (e.g. first click) so the AudioContext can start. */
  unlock(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    void ctx.resume().catch(() => {
      /* ignore; next gesture may succeed */
    });
  }

  private getAudioContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    const w = window as typeof window & { webkitAudioContext?: typeof AudioContext };
    const Ctor = window.AudioContext ?? w.webkitAudioContext;
    if (!Ctor) return null;
    if (!this.ctx) this.ctx = new Ctor();
    return this.ctx;
  }

  private playerAudioListener: THREE.AudioListener | null = null;

  setAudioListener(listener: THREE.AudioListener): void {
    this.playerAudioListener = listener;
  }

  /** Briefly lift one-shots and duck ambience so combat cues read clearly. */
  triggerCombatFocus(intensity = 1): void {
    this.combatFocus = Math.max(this.combatFocus, Math.max(0, Math.min(1.4, intensity)));
    this.applyMixGains();
  }

  /** Sharper, shorter mix carve for storm damage ticks. */
  triggerStormTickMix(): void {
    this.stormTickFocus = Math.max(this.stormTickFocus, 1);
    this.applyMixGains();
  }

  updateMix(dt: number): void {
    if (dt <= 0) return;
    const combatBefore = this.combatFocus;
    const stormBefore = this.stormTickFocus;
    this.combatFocus *= Math.exp(-dt * COMBAT_FOCUS_DECAY);
    this.stormTickFocus *= Math.exp(-dt * STORM_TICK_DECAY);
    if (this.combatFocus < 0.001) this.combatFocus = 0;
    if (this.stormTickFocus < 0.001) this.stormTickFocus = 0;
    if (combatBefore !== this.combatFocus || stormBefore !== this.stormTickFocus) {
      this.applyMixGains();
    }
  }

  private getCombatOutput(ctx: AudioContext): GainNode {
    if (!this.combatOut) {
      this.combatOut = ctx.createGain();
      this.combatOut.gain.value = 1;
      this.combatOut.connect(ctx.destination);
    }
    return this.combatOut;
  }

  private setGainTarget(gain: GainNode, value: number): void {
    const ctx = gain.context;
    const safeValue = Math.max(0.0001, value);
    gain.gain.cancelScheduledValues(ctx.currentTime);
    gain.gain.setTargetAtTime(safeValue, ctx.currentTime, 0.035);
  }

  private applyMixGains(): void {
    if (!this.ctx) return;
    const combat = Math.max(0, Math.min(1.4, this.combatFocus));
    const stormTick = Math.max(0, Math.min(1, this.stormTickFocus));
    const ambientDuck = Math.max(0.58, 1 - combat * 0.22 - stormTick * 0.3);

    if (this.combatOut) {
      this.setGainTarget(this.combatOut, 1 + combat * 0.22 + stormTick * 0.08);
    }
    if (this.lobbyOut && !this.lobbyStopping) {
      this.setGainTarget(this.lobbyOut, MASTER * this.masterMul * LOBBY_VOL * ambientDuck);
    }
    if (this.stormOut) {
      this.setGainTarget(this.stormOut, MASTER * this.masterMul * STORM_AMB_VOL * ambientDuck);
    }
  }

  private createMasterGain(): GainNode | null {
    const ctx = this.getAudioContext();
    if (!ctx) return null;
    const g = ctx.createGain();
    g.gain.value = MASTER * this.masterMul;
    g.connect(this.getCombatOutput(ctx));
    this.applyMixGains();
    return g;
  }

  /** Low rumbling flyover for supply drop plane (~1.4s). */
  playPlaneFlyover(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const out = this.createMasterGain();
    if (!out) return;
    const t0 = ctx.currentTime;
    const dur = 1.45;

    const bufferSize = Math.floor(ctx.sampleRate * dur);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.015 * white) / 1.015;
      const t = i / data.length;
      data[i] = last * (0.35 + Math.sin(t * Math.PI) * 0.65);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(420, t0);
    lp.frequency.exponentialRampToValueAtTime(1800, t0 + dur * 0.42);
    lp.frequency.exponentialRampToValueAtTime(280, t0 + dur);
    lp.Q.value = 0.55;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.52, t0 + 0.18);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(lp);
    lp.connect(g);
    g.connect(out);
    src.start(t0);
    src.stop(t0 + dur + 0.02);

    const rumble = ctx.createOscillator();
    const rumbleG = ctx.createGain();
    rumble.type = 'sine';
    rumble.frequency.setValueAtTime(48, t0);
    rumble.frequency.exponentialRampToValueAtTime(72, t0 + dur * 0.5);
    rumbleG.gain.setValueAtTime(0.0001, t0);
    rumbleG.gain.exponentialRampToValueAtTime(0.22, t0 + 0.12);
    rumbleG.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    rumble.connect(rumbleG);
    rumbleG.connect(out);
    rumble.start(t0);
    rumble.stop(t0 + dur + 0.02);

    const cleanup = () => {
      g.disconnect();
      lp.disconnect();
      src.disconnect();
      rumbleG.disconnect();
      rumble.disconnect();
    };
    src.onended = cleanup;
    rumble.onended = cleanup;
  }

  /** Soft looping pad for the lobby (procedural oscillators, low volume). */
  playLobbyMusic(): void {
    if (this.lobbyOut) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const out = ctx.createGain();
    out.gain.value = MASTER * this.masterMul * LOBBY_VOL;
    out.connect(ctx.destination);
    this.lobbyOut = out;
    this.lobbyStopping = false;
    this.applyMixGains();

    const freqs = [130.81, 164.81, 196.0, 261.63];
    const types: OscillatorType[] = ['sine', 'triangle', 'sine', 'triangle'];
    for (let i = 0; i < freqs.length; i++) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = types[i]!;
      osc.frequency.value = freqs[i]!;
      g.gain.value = 0.0001;
      osc.connect(g);
      g.connect(out);
      osc.start();
      this.lobbyVoices.push({ src: osc, gain: g });
    }
  }

  stopLobbyMusic(): void {
    if (this.lobbyOut && this.ctx) {
      const t = this.ctx.currentTime;
      this.lobbyStopping = true;
      this.lobbyOut.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
      for (const v of this.lobbyVoices) {
        v.src.stop(t + 1.25);
        // Clean up resources shortly after
        window.setTimeout(() => {
          v.src.disconnect();
          v.gain.disconnect();
        }, 1300);
      }
      window.setTimeout(() => {
        if (this.lobbyOut) {
          this.lobbyOut.disconnect();
          this.lobbyOut = null;
        }
        this.lobbyStopping = false;
      }, 1400);
    }
    this.lobbyVoices = [];
  }

  /** Rumbling low-frequency bed while outside the safe zone. */
  playStormAmbient(): void {
    if (this.stormOut) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const out = ctx.createGain();
    out.gain.value = MASTER * this.masterMul * STORM_AMB_VOL;
    out.connect(ctx.destination);
    this.stormOut = out;
    this.applyMixGains();

    const osc = ctx.createOscillator();
    const oscG = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 52;
    oscG.gain.value = 0.55;
    osc.connect(oscG);
    oscG.connect(out);
    osc.start();
    this.stormOsc = osc;

    const lfo = ctx.createOscillator();
    const lfoG = ctx.createGain();
    lfo.type = 'sine';
    lfo.frequency.value = 0.35;
    lfoG.gain.value = 14;
    lfo.connect(lfoG);
    lfoG.connect(osc.frequency);
    lfo.start();
    this.stormLfo = lfo;

    const dur = 2.4;
    const bufferSize = Math.floor(ctx.sampleRate * dur);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.5;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 120;
    lp.Q.value = 0.6;
    const nG = ctx.createGain();
    nG.gain.value = 0.42;
    src.connect(lp);
    lp.connect(nG);
    nG.connect(out);
    src.start();
    this.stormNoiseSrc = src;
  }

  stopStormAmbient(): void {
    const stopNode = (node: AudioScheduledSourceNode | null): void => {
      if (!node) return;
      try {
        node.stop();
        node.disconnect();
      } catch {
        /* ignore */
      }
    };
    stopNode(this.stormOsc);
    stopNode(this.stormLfo);
    stopNode(this.stormNoiseSrc);
    this.stormOsc = null;
    this.stormLfo = null;
    this.stormNoiseSrc = null;
    if (this.stormOut) {
      this.stormOut.disconnect();
      this.stormOut = null;
    }
  }

  /** Crisp metallic ping + ascending sting for headshot eliminations. */
  playHeadshotElim(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;

    const ping = ctx.createOscillator();
    const pingG = ctx.createGain();
    ping.type = 'sine';
    ping.frequency.setValueAtTime(1480, t0);
    ping.frequency.exponentialRampToValueAtTime(2880, t0 + 0.055);
    pingG.gain.setValueAtTime(0.0001, t0);
    pingG.gain.exponentialRampToValueAtTime(0.62, t0 + 0.004);
    pingG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
    ping.connect(pingG);
    pingG.connect(out);
    ping.start(t0);
    ping.stop(t0 + 0.16);

    const notes = [880, 1174.66, 1567.98];
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = f;
      const start = t0 + 0.028 + i * 0.038;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.38 - i * 0.06, start + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.22);
      osc.connect(g);
      g.connect(out);
      osc.start(start);
      osc.stop(start + 0.24);
      osc.onended = () => {
        g.disconnect();
        osc.disconnect();
      };
    });

    const shimmer = ctx.createOscillator();
    const shimmerG = ctx.createGain();
    shimmer.type = 'square';
    shimmer.frequency.setValueAtTime(3520, t0 + 0.04);
    shimmer.frequency.exponentialRampToValueAtTime(5280, t0 + 0.12);
    shimmerG.gain.setValueAtTime(0.0001, t0 + 0.04);
    shimmerG.gain.exponentialRampToValueAtTime(0.08, t0 + 0.05);
    shimmerG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
    shimmer.connect(shimmerG);
    shimmerG.connect(out);
    shimmer.start(t0 + 0.04);
    shimmer.stop(t0 + 0.2);

    const thump = ctx.createOscillator();
    const thumpG = ctx.createGain();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(110, t0);
    thump.frequency.exponentialRampToValueAtTime(55, t0 + 0.1);
    thumpG.gain.setValueAtTime(0.0001, t0);
    thumpG.gain.exponentialRampToValueAtTime(0.28, t0 + 0.006);
    thumpG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.12);
    thump.connect(thumpG);
    thumpG.connect(out);
    thump.start(t0);
    thump.stop(t0 + 0.14);

    const cleanup = () => {
      pingG.disconnect();
      ping.disconnect();
      shimmerG.disconnect();
      shimmer.disconnect();
      thumpG.disconnect();
      thump.disconnect();
    };
    ping.onended = cleanup;
    thump.onended = cleanup;
  }

  /** Triumphant fanfare when entering top 10 / top 5 / top 3. */
  playPlacementBanner(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;

    const thump = ctx.createOscillator();
    const thumpG = ctx.createGain();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(98, t0);
    thump.frequency.exponentialRampToValueAtTime(52, t0 + 0.12);
    thumpG.gain.setValueAtTime(0.0001, t0);
    thumpG.gain.exponentialRampToValueAtTime(0.38, t0 + 0.008);
    thumpG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
    thump.connect(thumpG);
    thumpG.connect(out);
    thump.start(t0);
    thump.stop(t0 + 0.15);

    const notes = [523.25, 659.25, 783.99, 987.77, 1174.66];
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = f;
      const start = t0 + 0.06 + i * 0.07;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.48 - i * 0.05, start + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.28);
      osc.connect(g);
      g.connect(out);
      osc.start(start);
      osc.stop(start + 0.3);
      osc.onended = () => {
        g.disconnect();
        osc.disconnect();
      };
    });

    const ping = ctx.createOscillator();
    const pingG = ctx.createGain();
    ping.type = 'sine';
    ping.frequency.setValueAtTime(1760, t0 + 0.38);
    ping.frequency.exponentialRampToValueAtTime(2349.32, t0 + 0.52);
    pingG.gain.setValueAtTime(0.0001, t0 + 0.38);
    pingG.gain.exponentialRampToValueAtTime(0.32, t0 + 0.39);
    pingG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.58);
    ping.connect(pingG);
    pingG.connect(out);
    ping.start(t0 + 0.38);
    ping.stop(t0 + 0.6);
    ping.onended = () => {
      pingG.disconnect();
      ping.disconnect();
      thumpG.disconnect();
      thump.disconnect();
    };
  }

  /** Triumphant ascending arpeggio on victory. */
  playVictoryStinger(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    const t0 = ctx.currentTime;
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = f;
      const start = t0 + i * 0.11;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.55, start + 0.025);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.42);
      osc.connect(g);
      g.connect(out);
      osc.start(start);
      osc.stop(start + 0.45);
      osc.onended = () => {
        g.disconnect();
        osc.disconnect();
      };
    });
  }

  /** Descending tone on defeat. */
  playDefeatStinger(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sawtooth';
    const t0 = ctx.currentTime;
    osc.frequency.setValueAtTime(220, t0);
    osc.frequency.exponentialRampToValueAtTime(55, t0 + 0.85);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.42, t0 + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.9);
    osc.connect(g);
    g.connect(out);
    osc.start(t0);
    osc.stop(t0 + 0.95);
    osc.onended = () => {
      g.disconnect();
      osc.disconnect();
    };
  }

  /** Fishing catch — splash plus a short upward chirp. */
  playFishCatch(): void {
    this.playSplash();
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(220, t0);
    o.frequency.exponentialRampToValueAtTime(640, t0 + 0.12);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.32, t0 + 0.018);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
    o.connect(g);
    g.connect(out);
    o.start(t0);
    o.stop(t0 + 0.17);
    o.onended = () => {
      g.disconnect();
      o.disconnect();
    };
  }

  /** Water entry splash — bandpassed noise + low bubble tone. */
  playSplash(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const dur = 0.22;
    const bufferSize = Math.max(128, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / data.length;
      data[i] = (Math.random() * 2 - 1) * (1 - t) * (1 - t);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 680;
    bp.Q.value = 0.75;
    const g = ctx.createGain();
    const t0 = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.62, t0 + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    const out = this.createMasterGain();
    if (!out) return;
    src.connect(bp);
    bp.connect(g);
    g.connect(out);
    src.start();
    src.stop(t0 + dur + 0.02);

    const o = ctx.createOscillator();
    const gO = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(180, t0);
    o.frequency.exponentialRampToValueAtTime(95, t0 + 0.14);
    gO.gain.setValueAtTime(0.0001, t0);
    gO.gain.exponentialRampToValueAtTime(0.28, t0 + 0.008);
    gO.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
    o.connect(gO);
    gO.connect(out);
    o.start(t0);
    o.stop(t0 + 0.18);
    const cleanup = () => {
      g.disconnect();
      bp.disconnect();
      src.disconnect();
      gO.disconnect();
      o.disconnect();
    };
    src.onended = cleanup;
    o.onended = cleanup;
  }

  /** Short upward chirp */
  playJump(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(180, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(520, ctx.currentTime + 0.045);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.9, ctx.currentTime + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);
    o.connect(g);
    g.connect(out);
    o.start(ctx.currentTime);
    o.stop(ctx.currentTime + 0.09);
    o.onended = () => {
      g.disconnect();
      o.disconnect();
    };
  }

  /** Very short bandpass click for foot contact; sprint is slightly louder. */
  playFootstep(sprint: boolean, surface: FootstepSurface = 'grass'): void {
    this.playFootstepAtVolume(sprint ? 0.38 : 0.18, sprint, surface);
  }

  /** Bot footstep with distance-based volume (0–1). Audible only when volume > 0. */
  playBotFootstep(volume: number): void {
    const v = Math.max(0, Math.min(1, volume));
    if (v < 0.02) return;
    this.playFootstepAtVolume(0.14 + v * 0.28, false, 'grass');
  }

  private playFootstepAtVolume(peak: number, sprint: boolean, surface: FootstepSurface = 'grass'): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const profiles: Record<FootstepSurface, { fCenter: number; q: number; peakMul: number }> = {
      grass: { fCenter: sprint ? 920 : 680, q: 0.7, peakMul: 1 },
      wood: { fCenter: sprint ? 1240 : 960, q: 1.05, peakMul: 1.06 },
      metal: { fCenter: sprint ? 1980 : 1580, q: 1.45, peakMul: 1.14 },
    };
    const profile = profiles[surface];
    const dur = sprint ? 0.036 : 0.028;
    const fCenter = profile.fCenter;
    peak *= profile.peakMul;
    const bufferSize = Math.max(64, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = fCenter;
    bp.Q.value = profile.q;
    const g = ctx.createGain();
    const t0 = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    const out = this.createMasterGain();
    if (!out) return;
    src.connect(bp);
    bp.connect(g);
    g.connect(out);
    src.start();
    src.stop(t0 + dur + 0.008);
    src.onended = () => {
      g.disconnect();
      bp.disconnect();
      src.disconnect();
    };
  }

  /** Subtle whoosh when the death recap card appears. */
  playDeathRecap(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const dur = 0.38;
    const bufferSize = Math.floor(ctx.sampleRate * dur);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / data.length;
      data[i] = (Math.random() * 2 - 1) * (1 - t * 0.85);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.Q.value = 0.35;
    const t0 = ctx.currentTime;
    lp.frequency.setValueAtTime(5200, t0);
    lp.frequency.exponentialRampToValueAtTime(420, t0 + dur * 0.88);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    const out = this.createMasterGain();
    if (!out) return;
    src.connect(lp);
    lp.connect(g);
    g.connect(out);
    src.start();
    src.stop(t0 + dur + 0.02);
    src.onended = () => {
      g.disconnect();
      lp.disconnect();
      src.disconnect();
    };
  }

  /** Short wind whoosh for glider open — noise + falling lowpass, not a jump chirp. */
  playGliderDeploy(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const dur = 0.32;
    const bufferSize = Math.floor(ctx.sampleRate * dur);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.Q.value = 0.45;
    const t0 = ctx.currentTime;
    lp.frequency.setValueAtTime(7200, t0);
    lp.frequency.exponentialRampToValueAtTime(380, t0 + dur * 0.92);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.48, t0 + 0.055);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    const out = this.createMasterGain();
    if (!out) return;
    src.connect(lp);
    lp.connect(g);
    g.connect(out);
    src.start();
    src.stop(t0 + dur + 0.02);
    src.onended = () => {
      g.disconnect();
      lp.disconnect();
      src.disconnect();
    };
  }

  /** Magazine release + slide when a reload begins. */
  playReload(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const out = this.createMasterGain();
    if (!out) return;

    const releaseDur = 0.028;
    const bufferSize = Math.max(32, Math.floor(ctx.sampleRate * releaseDur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1180;
    bp.Q.value = 1.1;

    const click = ctx.createOscillator();
    click.type = 'triangle';
    click.frequency.setValueAtTime(760, t0);
    click.frequency.exponentialRampToValueAtTime(420, t0 + releaseDur);

    const slide = ctx.createOscillator();
    slide.type = 'sine';
    slide.frequency.setValueAtTime(280, t0 + 0.012);
    slide.frequency.exponentialRampToValueAtTime(180, t0 + 0.09);

    const gNoise = ctx.createGain();
    gNoise.gain.setValueAtTime(0.0001, t0);
    gNoise.gain.exponentialRampToValueAtTime(0.13, t0 + 0.003);
    gNoise.gain.exponentialRampToValueAtTime(0.0001, t0 + releaseDur);

    const gClick = ctx.createGain();
    gClick.gain.setValueAtTime(0.0001, t0);
    gClick.gain.exponentialRampToValueAtTime(0.09, t0 + 0.002);
    gClick.gain.exponentialRampToValueAtTime(0.0001, t0 + releaseDur * 0.9);

    const gSlide = ctx.createGain();
    gSlide.gain.setValueAtTime(0.0001, t0 + 0.01);
    gSlide.gain.exponentialRampToValueAtTime(0.06, t0 + 0.018);
    gSlide.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.1);

    src.connect(bp);
    bp.connect(gNoise);
    gNoise.connect(out);
    click.connect(gClick);
    gClick.connect(out);
    slide.connect(gSlide);
    gSlide.connect(out);
    src.start(t0);
    src.stop(t0 + releaseDur + 0.006);
    click.start(t0);
    click.stop(t0 + releaseDur + 0.006);
    slide.start(t0 + 0.01);
    slide.stop(t0 + 0.11);
    const cleanup = () => {
      gNoise.disconnect();
      gClick.disconnect();
      gSlide.disconnect();
      bp.disconnect();
      src.disconnect();
      click.disconnect();
      slide.disconnect();
    };
    src.onended = cleanup;
    slide.onended = cleanup;
  }

  /** Equip snap when draw completes after holster. */
  playWeaponEquip(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const dur = 0.028;

    const tick = ctx.createOscillator();
    tick.type = 'triangle';
    tick.frequency.setValueAtTime(720, t0);
    tick.frequency.exponentialRampToValueAtTime(1040, t0 + dur);

    const gTick = ctx.createGain();
    gTick.gain.setValueAtTime(0.0001, t0);
    gTick.gain.exponentialRampToValueAtTime(0.1, t0 + 0.002);
    gTick.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    const out = this.createMasterGain();
    if (!out) return;
    tick.connect(gTick);
    gTick.connect(out);
    tick.start(t0);
    tick.stop(t0 + dur + 0.006);
    tick.onended = () => {
      gTick.disconnect();
      tick.disconnect();
    };
  }

  /** Subtle mechanical click when holstering a weapon. */
  playWeaponSwap(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const dur = 0.022;

    const bufferSize = Math.max(32, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1680;
    bp.Q.value = 1.35;

    const tick = ctx.createOscillator();
    tick.type = 'triangle';
    tick.frequency.setValueAtTime(920, t0);
    tick.frequency.exponentialRampToValueAtTime(640, t0 + dur);

    const gNoise = ctx.createGain();
    gNoise.gain.setValueAtTime(0.0001, t0);
    gNoise.gain.exponentialRampToValueAtTime(0.11, t0 + 0.002);
    gNoise.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    const gTick = ctx.createGain();
    gTick.gain.setValueAtTime(0.0001, t0);
    gTick.gain.exponentialRampToValueAtTime(0.07, t0 + 0.0015);
    gTick.gain.exponentialRampToValueAtTime(0.0001, t0 + dur * 0.85);

    const out = this.createMasterGain();
    if (!out) return;
    src.connect(bp);
    bp.connect(gNoise);
    gNoise.connect(out);
    tick.connect(gTick);
    gTick.connect(out);
    src.start(t0);
    src.stop(t0 + dur + 0.006);
    tick.start(t0);
    tick.stop(t0 + dur + 0.006);
    const cleanup = () => {
      gNoise.disconnect();
      gTick.disconnect();
      bp.disconnect();
      src.disconnect();
      tick.disconnect();
    };
    src.onended = cleanup;
    tick.onended = cleanup;
  }

  playShoot(kind: 'ar' | 'shotgun' | 'sniper' | 'smg' | 'pistol'): void {
    if (kind === 'ar') this.playBurst(0.038, [1400, 950], 'square');
    else if (kind === 'shotgun') this.playNoiseBurst(0.07, 0.55);
    else if (kind === 'sniper') this.playSniperCrack();
    else if (kind === 'smg') this.playBurst(0.028, [1800, 1100], 'square');
    else this.playBurst(0.034, [1200, 800], 'triangle');
  }

  /** Placement thud with pitch tiered by build material (wood low, metal high). */
  playBuildConfirm(mat: 'wood' | 'brick' | 'metal'): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;
    const freqs: Record<'wood' | 'brick' | 'metal', [number, number]> = {
      wood: [420, 280],
      brick: [620, 410],
      metal: [920, 640],
    };
    const [startHz, endHz] = freqs[mat];
    const thud = ctx.createOscillator();
    const click = ctx.createOscillator();
    const thudGain = ctx.createGain();
    const clickGain = ctx.createGain();
    thud.type = 'triangle';
    click.type = 'square';
    thud.frequency.setValueAtTime(startHz, t0);
    thud.frequency.exponentialRampToValueAtTime(Math.max(80, endHz), t0 + 0.07);
    click.frequency.setValueAtTime(startHz * 2.4, t0);
    click.frequency.exponentialRampToValueAtTime(Math.max(120, endHz * 2.1), t0 + 0.035);
    thudGain.gain.setValueAtTime(0.0001, t0);
    thudGain.gain.exponentialRampToValueAtTime(mat === 'metal' ? 0.62 : mat === 'brick' ? 0.54 : 0.48, t0 + 0.004);
    thudGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.11);
    clickGain.gain.setValueAtTime(0.0001, t0);
    clickGain.gain.exponentialRampToValueAtTime(0.22, t0 + 0.002);
    clickGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.045);
    thud.connect(thudGain);
    click.connect(clickGain);
    thudGain.connect(out);
    clickGain.connect(out);
    thud.start(t0);
    click.start(t0);
    thud.stop(t0 + 0.12);
    click.stop(t0 + 0.05);
    const cleanup = () => {
      thudGain.disconnect();
      clickGain.disconnect();
      thud.disconnect();
      click.disconnect();
    };
    thud.onended = cleanup;
    click.onended = cleanup;
  }

  /** Short clipped noise burst for impact feedback */
  playBuildBreak(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t = ctx.currentTime;
    
    const noise = ctx.createBufferSource();
    const len = ctx.sampleRate * 0.45;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.exp(-i / len * 4);
    }
    noise.buffer = buf;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(600, t);
    filter.frequency.exponentialRampToValueAtTime(150, t + 0.4);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.55, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.4);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(out);
    noise.start(t);
    noise.stop(t + 0.45);
  }

  playHitTier(kind: 'body' | 'crit' | 'headshot' | 'shield' | 'elimination' = 'body'): void {
    if (kind === 'headshot' || kind === 'elimination') {
      this.playHitHeadshot();
      return;
    }
    if (kind === 'crit') {
      this.playHitCrit();
      return;
    }
    if (kind === 'shield') {
      this.playHitShield();
      return;
    }
    this.playHit();
  }

  /** Bright tick for critical body hits. */
  playHitCrit(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;
    const ping = ctx.createOscillator();
    const g = ctx.createGain();
    ping.type = 'triangle';
    ping.frequency.setValueAtTime(1180, t0);
    ping.frequency.exponentialRampToValueAtTime(1680, t0 + 0.04);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.58, t0 + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.07);
    ping.connect(g);
    g.connect(out);
    ping.start(t0);
    ping.stop(t0 + 0.075);
    ping.onended = () => {
      g.disconnect();
      ping.disconnect();
    };
  }

  /** Sharper metallic ping for headshots. */
  playHitHeadshot(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;
    const ping = ctx.createOscillator();
    const g = ctx.createGain();
    ping.type = 'sine';
    ping.frequency.setValueAtTime(1560, t0);
    ping.frequency.exponentialRampToValueAtTime(2480, t0 + 0.05);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.68, t0 + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.1);
    ping.connect(g);
    g.connect(out);
    ping.start(t0);
    ping.stop(t0 + 0.105);
    ping.onended = () => {
      g.disconnect();
      ping.disconnect();
    };
  }

  /** Soft glassy tick for shield hits. */
  playHitShield(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;
    const ping = ctx.createOscillator();
    const g = ctx.createGain();
    ping.type = 'sine';
    ping.frequency.setValueAtTime(920, t0);
    ping.frequency.exponentialRampToValueAtTime(1320, t0 + 0.035);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.48, t0 + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.06);
    ping.connect(g);
    g.connect(out);
    ping.start(t0);
    ping.stop(t0 + 0.065);
    ping.onended = () => {
      g.disconnect();
      ping.disconnect();
    };
  }

  playHit(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const dur = 0.05;
    const bufferSize = Math.max(64, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 900;
    bp.Q.value = 0.7;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.65, ctx.currentTime + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    const out = this.createMasterGain();
    if (!out) return;
    src.connect(bp);
    bp.connect(g);
    g.connect(out);
    src.start();
    src.stop(ctx.currentTime + dur + 0.01);
    src.onended = () => {
      g.disconnect();
      bp.disconnect();
      src.disconnect();
    };
  }

  /** Tiny two-tone "open" shimmer */
  /** Ascending reboot/resurrection chime at the van. */
  playRebootVan(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;

    const mk = (t: number, f: number, peak: number, dur: number) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, t);
      osc.frequency.exponentialRampToValueAtTime(f * 1.08, t + dur * 0.55);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(peak, t + 0.018);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(g);
      g.connect(out);
      osc.start(t);
      osc.stop(t + dur + 0.02);
      osc.onended = () => {
        g.disconnect();
        osc.disconnect();
      };
    };

    mk(t0, 440, 0.42, 0.22);
    mk(t0 + 0.08, 554.37, 0.38, 0.24);
    mk(t0 + 0.16, 659.25, 0.34, 0.28);
    mk(t0 + 0.24, 880, 0.3, 0.32);

    const shimmer = ctx.createOscillator();
    const shimmerG = ctx.createGain();
    shimmer.type = 'triangle';
    shimmer.frequency.setValueAtTime(1760, t0 + 0.12);
    shimmer.frequency.exponentialRampToValueAtTime(2200, t0 + 0.45);
    shimmerG.gain.setValueAtTime(0.0001, t0 + 0.12);
    shimmerG.gain.exponentialRampToValueAtTime(0.12, t0 + 0.18);
    shimmerG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.48);
    shimmer.connect(shimmerG);
    shimmerG.connect(out);
    shimmer.start(t0 + 0.12);
    shimmer.stop(t0 + 0.5);
    shimmer.onended = () => {
      shimmerG.disconnect();
      shimmer.disconnect();
    };
  }

  playChest(tier: 'normal' | 'rare' = 'normal'): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const rare = tier === 'rare';

    const mk = (t: number, f: number, peak = 0.45) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(peak, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
      osc.connect(g);
      g.connect(out);
      osc.start(t);
      osc.stop(t + 0.18);
      osc.onended = () => {
        g.disconnect();
        osc.disconnect();
      };
    };

    const t0 = ctx.currentTime;
    mk(t0, rare ? 440 : 392, rare ? 0.5 : 0.45);
    mk(t0 + 0.035, rare ? 587.33 : 523.25, rare ? 0.46 : 0.45);
    if (rare) mk(t0 + 0.07, 698.46, 0.38);

    const shimmer = ctx.createOscillator();
    const shimmerG = ctx.createGain();
    shimmer.type = 'triangle';
    shimmer.frequency.setValueAtTime(rare ? 1760 : 1318.51, t0 + 0.05);
    shimmer.frequency.exponentialRampToValueAtTime(rare ? 2349 : 1760, t0 + 0.34);
    shimmerG.gain.setValueAtTime(0.0001, t0 + 0.05);
    shimmerG.gain.exponentialRampToValueAtTime(rare ? 0.14 : 0.09, t0 + 0.1);
    shimmerG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.38);
    shimmer.connect(shimmerG);
    shimmerG.connect(out);
    shimmer.start(t0 + 0.05);
    shimmer.stop(t0 + 0.4);
    shimmer.onended = () => {
      shimmerG.disconnect();
      shimmer.disconnect();
    };
  }

  /** Satisfying ascending chime when buying a weapon from the vending machine. */
  playVendWeapon(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;

    const notes = [523.25, 659.25, 783.99, 987.77];
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, t0 + i * 0.055);
      osc.frequency.exponentialRampToValueAtTime(f * 1.04, t0 + i * 0.055 + 0.12);
      g.gain.setValueAtTime(0.0001, t0 + i * 0.055);
      g.gain.exponentialRampToValueAtTime(0.44 - i * 0.05, t0 + i * 0.055 + 0.014);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + i * 0.055 + 0.2);
      osc.connect(g);
      g.connect(out);
      osc.start(t0 + i * 0.055);
      osc.stop(t0 + i * 0.055 + 0.22);
      osc.onended = () => {
        g.disconnect();
        osc.disconnect();
      };
    });

    const ping = ctx.createOscillator();
    const pingG = ctx.createGain();
    ping.type = 'triangle';
    ping.frequency.setValueAtTime(1318.51, t0 + 0.2);
    ping.frequency.exponentialRampToValueAtTime(1760, t0 + 0.32);
    pingG.gain.setValueAtTime(0.0001, t0 + 0.2);
    pingG.gain.exponentialRampToValueAtTime(0.28, t0 + 0.21);
    pingG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.38);
    ping.connect(pingG);
    pingG.connect(out);
    ping.start(t0 + 0.2);
    ping.stop(t0 + 0.4);
    ping.onended = () => {
      pingG.disconnect();
      ping.disconnect();
    };
  }

  /** Metallic anvil tap when crafting at a bench. */
  playCraft(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;

    const bufferSize = Math.max(128, Math.floor(ctx.sampleRate * 0.12));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / data.length;
      data[i] = (Math.random() * 2 - 1) * (1 - t) * (1 - t);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(820, t0);
    bp.frequency.exponentialRampToValueAtTime(420, t0 + 0.08);
    bp.Q.value = 1.4;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.55, t0 + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.11);
    src.connect(bp);
    bp.connect(g);
    g.connect(out);
    src.start(t0);
    src.stop(t0 + 0.12);

    const ring = ctx.createOscillator();
    const ringG = ctx.createGain();
    ring.type = 'triangle';
    ring.frequency.setValueAtTime(280, t0);
    ring.frequency.exponentialRampToValueAtTime(180, t0 + 0.14);
    ringG.gain.setValueAtTime(0.0001, t0);
    ringG.gain.exponentialRampToValueAtTime(0.22, t0 + 0.008);
    ringG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
    ring.connect(ringG);
    ringG.connect(out);
    ring.start(t0);
    ring.stop(t0 + 0.17);

    const cleanup = () => {
      g.disconnect();
      bp.disconnect();
      src.disconnect();
      ringG.disconnect();
      ring.disconnect();
    };
    src.onended = cleanup;
    ring.onended = cleanup;
  }

  /** Low rumble sting when the storm enters final-circle crunch. */
  playFinalCircle(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const out = this.createMasterGain();
    if (!out) return;
    const t0 = ctx.currentTime;
    const dur = 0.95;

    const rumble = ctx.createOscillator();
    const rumbleG = ctx.createGain();
    rumble.type = 'sine';
    rumble.frequency.setValueAtTime(62, t0);
    rumble.frequency.exponentialRampToValueAtTime(38, t0 + dur * 0.55);
    rumble.frequency.exponentialRampToValueAtTime(52, t0 + dur);
    rumbleG.gain.setValueAtTime(0.0001, t0);
    rumbleG.gain.exponentialRampToValueAtTime(0.48, t0 + 0.08);
    rumbleG.gain.setValueAtTime(0.42, t0 + dur * 0.45);
    rumbleG.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    rumble.connect(rumbleG);
    rumbleG.connect(out);
    rumble.start(t0);
    rumble.stop(t0 + dur + 0.02);

    const bufferSize = Math.max(128, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      const t = i / data.length;
      const white = Math.random() * 2 - 1;
      last = (last + 0.018 * white) / 1.018;
      data[i] = last * (0.4 + Math.sin(t * Math.PI) * 0.6);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(280, t0);
    lp.frequency.exponentialRampToValueAtTime(90, t0 + dur);
    lp.Q.value = 0.55;
    const nG = ctx.createGain();
    nG.gain.setValueAtTime(0.0001, t0);
    nG.gain.exponentialRampToValueAtTime(0.38, t0 + 0.06);
    nG.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(lp);
    lp.connect(nG);
    nG.connect(out);
    src.start(t0);
    src.stop(t0 + dur + 0.02);

    const thump = ctx.createOscillator();
    const thumpG = ctx.createGain();
    thump.type = 'triangle';
    thump.frequency.setValueAtTime(88, t0);
    thump.frequency.exponentialRampToValueAtTime(44, t0 + 0.22);
    thumpG.gain.setValueAtTime(0.0001, t0);
    thumpG.gain.exponentialRampToValueAtTime(0.32, t0 + 0.012);
    thumpG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.28);
    thump.connect(thumpG);
    thumpG.connect(out);
    thump.start(t0);
    thump.stop(t0 + 0.3);

    const cleanup = () => {
      rumbleG.disconnect();
      rumble.disconnect();
      nG.disconnect();
      lp.disconnect();
      src.disconnect();
      thumpG.disconnect();
      thump.disconnect();
    };
    rumble.onended = cleanup;
    src.onended = cleanup;
    thump.onended = cleanup;
  }

  /** Sparse low tick inside storm */
  playStormTick(loud = false): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(loud ? 145 : 110, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(loud ? 72 : 55, ctx.currentTime + 0.04);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(loud ? 0.62 : 0.35, ctx.currentTime + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + (loud ? 0.09 : 0.06));
    o.connect(g);
    g.connect(out);
    o.start(ctx.currentTime);
    o.stop(ctx.currentTime + (loud ? 0.1 : 0.07));
    o.onended = () => {
      g.disconnect();
      o.disconnect();
    };
  }

  /** Pinata-style burst when a loot llama is broken open. */
  playLlamaBreak(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const dur = 0.18;
    const bufferSize = Math.max(96, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / data.length;
      data[i] = (Math.random() * 2 - 1) * (1 - t) * (1 - t);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(3200, ctx.currentTime);
    bp.frequency.exponentialRampToValueAtTime(680, ctx.currentTime + dur * 0.85);
    bp.Q.value = 1.4;
    const g = ctx.createGain();
    const t0 = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.48, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    const out = this.createMasterGain();
    if (!out) return;
    src.connect(bp);
    bp.connect(g);
    g.connect(out);
    src.start();
    src.stop(t0 + dur + 0.015);

    const notes = [523.25, 659.25, 783.99];
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const og = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = f;
      const start = t0 + 0.04 + i * 0.045;
      og.gain.setValueAtTime(0.0001, start);
      og.gain.exponentialRampToValueAtTime(0.22, start + 0.012);
      og.gain.exponentialRampToValueAtTime(0.0001, start + 0.14);
      osc.connect(og);
      og.connect(out);
      osc.start(start);
      osc.stop(start + 0.16);
      osc.onended = () => {
        og.disconnect();
        osc.disconnect();
      };
    });

    src.onended = () => {
      g.disconnect();
      bp.disconnect();
      src.disconnect();
    };
  }

  /** Crisp glass shatter: short bandpassed noise burst (~0.12s). */
  playShieldBreak(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const dur = 0.12;
    const bufferSize = Math.max(64, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 5800;
    bp.Q.value = 3.2;
    const g = ctx.createGain();
    const t0 = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.38, t0 + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    const out = this.createMasterGain();
    if (!out) return;
    src.connect(bp);
    bp.connect(g);
    g.connect(out);
    src.start();
    src.stop(t0 + dur + 0.012);
    src.onended = () => {
      g.disconnect();
      bp.disconnect();
      src.disconnect();
    };
  }

  /** Short exertion grunt when picking up a knocked squadmate. */
  playCarryPickup(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;

    const grunt = ctx.createOscillator();
    const gruntG = ctx.createGain();
    grunt.type = 'triangle';
    grunt.frequency.setValueAtTime(118, t0);
    grunt.frequency.exponentialRampToValueAtTime(72, t0 + 0.09);
    gruntG.gain.setValueAtTime(0.0001, t0);
    gruntG.gain.exponentialRampToValueAtTime(0.34, t0 + 0.012);
    gruntG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.11);
    grunt.connect(gruntG);
    gruntG.connect(out);
    grunt.start(t0);
    grunt.stop(t0 + 0.12);

    const bufferSize = Math.max(32, Math.floor(ctx.sampleRate * 0.05));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 280;
    bp.Q.value = 0.9;
    const nG = ctx.createGain();
    nG.gain.setValueAtTime(0.0001, t0);
    nG.gain.exponentialRampToValueAtTime(0.14, t0 + 0.006);
    nG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.05);
    src.connect(bp);
    bp.connect(nG);
    nG.connect(out);
    src.start(t0);
    src.stop(t0 + 0.055);

    const cleanup = () => {
      gruntG.disconnect();
      grunt.disconnect();
      nG.disconnect();
      bp.disconnect();
      src.disconnect();
    };
    grunt.onended = cleanup;
    src.onended = cleanup;
  }

  /** Low body thump with quick downward pitch (quieter than playHit). */
  playPlayerHurt(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    const t0 = ctx.currentTime;
    o.frequency.setValueAtTime(155, t0);
    o.frequency.exponentialRampToValueAtTime(62, t0 + 0.11);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.36, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
    o.connect(g);
    g.connect(out);
    o.start(t0);
    o.stop(t0 + 0.15);
    o.onended = () => {
      g.disconnect();
      o.disconnect();
    };
  }

  /** Rising/falling siren when the zone is about to shrink (~1.1s). */
  playStormSiren(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const t0 = ctx.currentTime;
    const dur = 1.08;
    const cycles = 3;

    for (let c = 0; c < cycles; c++) {
      const start = t0 + c * (dur / cycles);
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(420, start);
      osc.frequency.linearRampToValueAtTime(780, start + dur / (cycles * 2));
      osc.frequency.linearRampToValueAtTime(420, start + dur / cycles);
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.28, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + dur / cycles - 0.01);
      osc.connect(g);
      g.connect(out);
      osc.start(start);
      osc.stop(start + dur / cycles + 0.02);
      osc.onended = () => {
        g.disconnect();
        osc.disconnect();
      };
    }
  }

  /** Sharp crack + rumble when storm lightning strikes near the player (~0.35s). */
  playStormStrike(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const out = this.createMasterGain();
    if (!out) return;
    const t0 = ctx.currentTime;
    const dur = 0.36;

    const bufferSize = Math.max(128, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / data.length;
      data[i] = (Math.random() * 2 - 1) * (1 - t) * (1 - t);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 1200;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.62, t0 + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.09);
    src.connect(hp);
    hp.connect(g);
    g.connect(out);
    src.start(t0);
    src.stop(t0 + 0.1);

    const rumble = ctx.createOscillator();
    const rumbleG = ctx.createGain();
    rumble.type = 'sawtooth';
    rumble.frequency.setValueAtTime(110, t0);
    rumble.frequency.exponentialRampToValueAtTime(42, t0 + dur);
    rumbleG.gain.setValueAtTime(0.0001, t0);
    rumbleG.gain.exponentialRampToValueAtTime(0.38, t0 + 0.012);
    rumbleG.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    rumble.connect(rumbleG);
    rumbleG.connect(out);
    rumble.start(t0);
    rumble.stop(t0 + dur + 0.02);

    src.onended = () => {
      g.disconnect();
      hp.disconnect();
      src.disconnect();
    };
    rumble.onended = () => {
      rumbleG.disconnect();
      rumble.disconnect();
    };
  }

  /** Urgent minor-third double beep for zone closing (~0.22s). */
  playStormClosing(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const fLo = 620;
    const fHi = fLo * Math.pow(2, 3 / 12);
    const mk = (start: number, f: number) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = f;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.34, start + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.085);
      osc.connect(g);
      g.connect(out);
      osc.start(start);
      osc.stop(start + 0.09);
      osc.onended = () => {
        g.disconnect();
        osc.disconnect();
      };
    };
    const t0 = ctx.currentTime;
    mk(t0, fLo);
    mk(t0 + 0.11, fHi);
  }

  /** Falling whistle during airstrike wind-up (~2.4s), then caller plays booms on impact. */
  playAirstrikeIncoming(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const out = this.createMasterGain();
    if (!out) return;
    const t0 = ctx.currentTime;
    const dur = 2.35;

    const whistle = ctx.createOscillator();
    const whistleG = ctx.createGain();
    whistle.type = 'sine';
    whistle.frequency.setValueAtTime(920, t0);
    whistle.frequency.exponentialRampToValueAtTime(280, t0 + dur * 0.92);
    whistleG.gain.setValueAtTime(0.0001, t0);
    whistleG.gain.exponentialRampToValueAtTime(0.34, t0 + 0.08);
    whistleG.gain.setValueAtTime(0.3, t0 + dur * 0.55);
    whistleG.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    whistle.connect(whistleG);
    whistleG.connect(out);
    whistle.start(t0);
    whistle.stop(t0 + dur + 0.02);

    const bufferSize = Math.max(128, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / data.length;
      data[i] = (Math.random() * 2 - 1) * (0.35 + t * 0.45) * (1 - t * 0.15);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.setValueAtTime(180, t0);
    hp.frequency.exponentialRampToValueAtTime(680, t0 + dur * 0.75);
    hp.Q.value = 0.5;
    const nG = ctx.createGain();
    nG.gain.setValueAtTime(0.0001, t0);
    nG.gain.exponentialRampToValueAtTime(0.18, t0 + 0.12);
    nG.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(hp);
    hp.connect(nG);
    nG.connect(out);
    src.start(t0);
    src.stop(t0 + dur + 0.02);

    const cleanup = () => {
      whistleG.disconnect();
      whistle.disconnect();
      nG.disconnect();
      hp.disconnect();
      src.disconnect();
    };
    whistle.onended = cleanup;
    src.onended = cleanup;
  }

  /** Low thump + noise burst for grenade / explosive detonation. */
  playExplosion(): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    const dur = 0.28;
    const bufferSize = Math.max(128, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / data.length;
      data[i] = (Math.random() * 2 - 1) * (1 - t) * (1 - t);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(2200, ctx.currentTime);
    lp.frequency.exponentialRampToValueAtTime(120, ctx.currentTime + dur * 0.85);
    lp.Q.value = 0.6;
    const g = ctx.createGain();
    const t0 = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.72, t0 + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    const out = this.createMasterGain();
    if (!out) return;
    src.connect(lp);
    lp.connect(g);
    g.connect(out);
    src.start();
    src.stop(t0 + dur + 0.02);
    const o = ctx.createOscillator();
    const gO = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(95, t0);
    o.frequency.exponentialRampToValueAtTime(28, t0 + 0.16);
    gO.gain.setValueAtTime(0.0001, t0);
    gO.gain.exponentialRampToValueAtTime(0.42, t0 + 0.01);
    gO.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.2);
    o.connect(gO);
    gO.connect(out);
    o.start(t0);
    o.stop(t0 + 0.22);
    const cleanup = () => {
      g.disconnect();
      lp.disconnect();
      src.disconnect();
      gO.disconnect();
      o.disconnect();
    };
    src.onended = cleanup;
    o.onended = cleanup;
  }

  private playBurst(dur: number, freqs: [number, number], type: OscillatorType): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freqs[0], ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(freqs[1], ctx.currentTime + dur);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.55, ctx.currentTime + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur + 0.02);
    o.connect(g);
    g.connect(out);
    o.start(ctx.currentTime);
    o.stop(ctx.currentTime + dur + 0.025);
    o.onended = () => {
      g.disconnect();
      o.disconnect();
    };
  }

  private playNoiseBurst(dur: number, filterQ: number): void {
    const ctx = this.getAudioContext();
    if (!ctx) return;

    const bufferSize = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;

    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2400;
    lp.Q.value = filterQ;

    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.5, ctx.currentTime + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);

    const out = this.createMasterGain();
    if (!out) return;
    src.connect(lp);
    lp.connect(g);
    g.connect(out);
    src.start();
    src.stop(ctx.currentTime + dur + 0.015);
    src.onended = () => {
      g.disconnect();
      lp.disconnect();
      src.disconnect();
    };
  }

  // ─── Spatial Audio Infrastructure ────────────────────────────────────────

  private createSpatialSource(x: number, y: number, z: number): PannerNode | null {
    const ctx = this.getAudioContext();
    if (!ctx) return null;
    const panner = ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = 5;
    panner.maxDistance = 90;
    panner.rolloffFactor = 1.4;
    panner.positionX.value = x;
    panner.positionY.value = y;
    panner.positionZ.value = z;
    panner.connect(this.getCombatOutput(ctx));
    this.applyMixGains();
    return panner;
  }

  public updateListener(pos: THREE.Vector3, forward: THREE.Vector3, up: THREE.Vector3): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const L = ctx.listener;
    if (L.positionX) {
      L.positionX.value = pos.x;
      L.positionY.value = pos.y;
      L.positionZ.value = pos.z;
      L.forwardX.value = forward.x;
      L.forwardY.value = forward.y;
      L.forwardZ.value = forward.z;
      L.upX.value = up.x;
      L.upY.value = up.y;
      L.upZ.value = up.z;
    } else {
      (L as AudioListener & { setPosition?: (x: number, y: number, z: number) => void }).setPosition?.(pos.x, pos.y, pos.z);
      (L as AudioListener & { setOrientation?: (fx: number, fy: number, fz: number, ux: number, uy: number, uz: number) => void }).setOrientation?.(forward.x, forward.y, forward.z, up.x, up.y, up.z);
    }
  }

  /** Positional gunshot — synthesizes like playShoot('ar') but routed through a PannerNode. */
  public playGunshotAt(pos: THREE.Vector3, kind?: string): void {
    const ctx = this.getAudioContext();
    if (!ctx || ctx.state === 'suspended') return;
    const panner = this.createSpatialSource(pos.x, pos.y, pos.z);
    if (!panner) return;

    // Use the same burst synthesis as playShoot('ar') / playBurst
    const dur = kind === 'sniper' ? 0.038 : kind === 'shotgun' ? 0.07 : kind === 'smg' ? 0.028 : 0.038;
    const freqHi = kind === 'sniper' ? 2200 : kind === 'shotgun' ? 2400 : kind === 'smg' ? 1800 : 1400;
    const freqLo = kind === 'sniper' ? 120  : kind === 'shotgun' ? 400  : kind === 'smg' ? 1100 : 950;
    const oscType: OscillatorType = kind === 'smg' ? 'square' : kind === 'sniper' ? 'sawtooth' : 'square';

    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = oscType;
    o.frequency.setValueAtTime(freqHi, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(freqLo, ctx.currentTime + dur);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.55, ctx.currentTime + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur + 0.02);
    o.connect(g);
    g.connect(panner);
    o.start(ctx.currentTime);
    o.stop(ctx.currentTime + dur + 0.025);
    o.onended = () => {
      g.disconnect();
      o.disconnect();
      panner.disconnect();
    };
  }

  /** Positional footstep — same synthesis as playFootstepAtVolume but routed through a PannerNode. */
  public playFootstepAt(pos: THREE.Vector3, sprint: boolean): void {
    const ctx = this.getAudioContext();
    if (!ctx || ctx.state === 'suspended') return;
    const panner = this.createSpatialSource(pos.x, pos.y, pos.z);
    if (!panner) return;

    const peak = sprint ? 0.38 : 0.18;
    const dur = sprint ? 0.036 : 0.028;
    const fCenter = sprint ? 1550 : 1050;
    const bufferSize = Math.max(64, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = fCenter;
    bp.Q.value = sprint ? 1.1 : 0.85;
    const g = ctx.createGain();
    const t0 = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(bp);
    bp.connect(g);
    g.connect(panner);
    src.start();
    src.stop(t0 + dur + 0.008);
    src.onended = () => {
      g.disconnect();
      bp.disconnect();
      src.disconnect();
      panner.disconnect();
    };
  }

  // ─── Low-HP Heartbeat ────────────────────────────────────────────────────

  private heartbeatIntervalId: ReturnType<typeof setInterval> | null = null;

  public startHeartbeat(bpm: number): void {
    if (this.heartbeatIntervalId) return; // already running
    const interval = 60000 / bpm;
    this.heartbeatIntervalId = setInterval(() => {
      const ctx = this.ctx;
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 62;
      g.gain.setValueAtTime(0, ctx.currentTime);
      g.gain.linearRampToValueAtTime(0.4, ctx.currentTime + 0.06);
      g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.12);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.14);
      osc.onended = () => {
        g.disconnect();
        osc.disconnect();
      };
    }, interval);
  }

  public stopHeartbeat(): void {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId);
      this.heartbeatIntervalId = null;
    }
  }

  // ─── Proximity Hiss ──────────────────────────────────────────────────────

  private hissGain: GainNode | null = null;
  private hissSource: OscillatorNode | null = null;

  public initProximityHiss(): void {
    const ctx = this.getAudioContext();
    if (this.hissGain || !ctx) return;
    this.hissGain = ctx.createGain();
    this.hissGain.gain.value = 0;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = 4000;
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 3500;
    osc.connect(filter);
    filter.connect(this.hissGain);
    this.hissGain.connect(ctx.destination);
    osc.start();
    this.hissSource = osc;
  }

  public setProximityHiss(intensity: number): void {
    if (!this.hissGain) this.initProximityHiss();
    if (this.hissGain) {
      this.hissGain.gain.value = Math.max(0, Math.min(1, intensity)) * 0.12;
    }
  }

  private playSniperCrack(): void {
    const out = this.createMasterGain();
    if (!out) return;
    const ctx = out.context;

    const nSrc = ctx.createBufferSource();
    const nBuf = Math.floor(ctx.sampleRate * 0.04);
    const buf = ctx.createBuffer(1, nBuf, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    nSrc.buffer = buf;

    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(2200, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(120, ctx.currentTime + 0.038);

    const gN = ctx.createGain();
    gN.gain.setValueAtTime(0.5, ctx.currentTime);
    gN.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.03);

    const gO = ctx.createGain();
    gO.gain.setValueAtTime(0.0001, ctx.currentTime);
    gO.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.002);
    gO.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.026);

    nSrc.connect(gN);
    gN.connect(out);
    o.connect(gO);
    gO.connect(out);

    nSrc.start();
    nSrc.stop(ctx.currentTime + 0.045);
    o.start(ctx.currentTime);
    o.stop(ctx.currentTime + 0.035);

    const cleanup = () => {
      gN.disconnect();
      gO.disconnect();
      nSrc.disconnect();
      o.disconnect();
    };
    nSrc.onended = cleanup;
    o.onended = cleanup;
  }
}
